import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ProcessObject, ProcessObjectListLoaderResponse } from '../list/types';
import { useLoaderData, useRevalidator } from 'react-router';
import { RHFAutoComplete } from '@/components/auto-complete/RHFAutoComplete';
import { useMutation } from '@tanstack/react-query';
import { getTtmsLabel } from '../list/constants';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import Spinner from '@/components/spinner/Spinner';
import { LINE_VOLTAGE_OPTIONS } from '../create-process-object/create-process-object-schema';
import { EditProcessObjectFormSchema, editProcessObjectSchema } from './edit-process-object-schema';
import { editProcessObject, getEditProcessObjectPayload } from './constants';
import { EditProcessObjectRequest } from './types';
import { ALGORITHM_LIST_OPTIONS } from '@/routes/calibration-task/constants';

export type EditProcessObjectFormProps = {
	isOpen: boolean;
	handleToggleForm: () => void;
	processObject: ProcessObject;
};
export const EditProcessObjectForm = (props: EditProcessObjectFormProps) => {
	const { isOpen, handleToggleForm, processObject } = props;
	const revalidator = useRevalidator();

	const {
		ttmsList: { data: ttmsListItems }
	} = useLoaderData() as ProcessObjectListLoaderResponse;

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const selectedPrimaryTtms = ttmsListItems.find(ttms => ttms.id === processObject.ttmsIdPrimary);
	const selectedSecondaryTtms = ttmsListItems.find(ttms => ttms.id === processObject.ttmsIdSecondary);

	const editProcessObjectForm = useForm<EditProcessObjectFormSchema>({
		resolver: zodResolver(editProcessObjectSchema),
		defaultValues: {
			cx: processObject.cx,
			cy: processObject.cy,
			trmsLimit: processObject.trmsLimit,
			timeAdjustment: processObject.timeAdjustment,
			voltage: [{ value: processObject.kvClass.toString(), label: processObject.kvClass.toString() }],
			primaryTtms: [{ value: selectedPrimaryTtms?.id.toString(), label: getTtmsLabel(selectedPrimaryTtms!) }],
			secondaryTtms: [
				{ value: selectedSecondaryTtms?.id.toString(), label: getTtmsLabel(selectedSecondaryTtms!) }
			],
			algorithm: [{ value: processObject.algorithm.toString(), label: processObject.algorithm.toString() }]
		}
	});
	const { isDesktop } = useIsDesktopSize();

	const { isPending: isSaving, mutate: mutateCreateProcessObject } = useMutation({
		mutationFn: (val: EditProcessObjectRequest) => editProcessObject(processObject.id, val),
		mutationKey: ['process-objects'],
		onSuccess: () => {
			editProcessObjectForm.reset();
			handleToggleForm();
			setShowConfirmation(false);
			toast('Process object updated successfully');
			revalidator.revalidate();
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleSaveProcessObject = () => {
		const submitForm = editProcessObjectForm.handleSubmit(values => {
			const payload = getEditProcessObjectPayload(processObject, values);
			mutateCreateProcessObject(payload);
		});
		submitForm();
	};

	const handleCloseForm = () => {
		handleToggleForm();
		setShowConfirmation(false);
		setError('');
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn('h-full p-5 overflow-auto', { 'sm:max-w-xl ': isDesktop, 'w-full h-5/6': !isDesktop })}
			>
				<SheetHeader>
					<SheetTitle>Edit process object</SheetTitle>
					<SheetDescription>Fill in the details. Click save when you're done.</SheetDescription>
				</SheetHeader>
				<Form {...editProcessObjectForm}>
					<form className="p-4 flex flex-col gap-3">
						<FormField
							name="cx"
							control={editProcessObjectForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>cX</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder="cX"
											onChange={e => field.onChange(parseInt(e.target.value))}
											onBlur={field.onBlur}
											name={field.name}
											value={field.value}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							name="cy"
							control={editProcessObjectForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>cY</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder="cY"
											onChange={e => field.onChange(parseInt(e.target.value))}
											onBlur={field.onBlur}
											name={field.name}
											value={field.value}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="trmsLimit"
							control={editProcessObjectForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>TRMS Limit</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder="TRMS Limit"
											onChange={e => field.onChange(parseInt(e.target.value))}
											onBlur={field.onBlur}
											name={field.name}
											value={field.value}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="timeAdjustment"
							control={editProcessObjectForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Time adjustment</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder="Time adjustment"
											onChange={e => field.onChange(parseInt(e.target.value))}
											onBlur={field.onBlur}
											name={field.name}
											value={field.value}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<RHFAutoComplete
							control={editProcessObjectForm.control}
							name="voltage"
							label="Line voltage"
							placeholder="Select line voltage"
							items={LINE_VOLTAGE_OPTIONS}
							closeOnSelect
							itemToOption={item => ({
								value: item.toString(),
								label: item.toString()
							})}
						/>

						<RHFAutoComplete
							control={editProcessObjectForm.control}
							name="primaryTtms"
							label="Primary TTMS"
							placeholder="Select primary TTMS"
							items={ttmsListItems}
							closeOnSelect
							itemToOption={item => ({
								value: item.id.toString(),
								label: getTtmsLabel(item)
							})}
						/>

						<RHFAutoComplete
							control={editProcessObjectForm.control}
							name="secondaryTtms"
							label="Secondary TTMS"
							placeholder="Select secondary TTMS"
							items={ttmsListItems}
							closeOnSelect
							itemToOption={item => ({
								value: item.id.toString(),
								label: getTtmsLabel(item)
							})}
						/>

						<RHFAutoComplete
							control={editProcessObjectForm.control}
							name="algorithm"
							label="Algorithm"
							placeholder="Select algorithm"
							items={ALGORITHM_LIST_OPTIONS}
							itemToOption={item => ({
								value: item.id.toString(),
								label: item.id.toString()
							})}
						/>
					</form>
				</Form>
				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}
				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Save changes
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>Are you sure you want to edit this process object?</p>
							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isSaving}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={handleSaveProcessObject}
									disabled={isSaving}
								>
									{error ? 'Try again ' : 'Save'}
									{isSaving && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};
